/* 🔔 بانر تفعيل الإشعارات — يرشد الآيفون لتثبيت PWA ويفعّل الاشتراك بضغطة */
import React, { useEffect, useState } from "react";
import { Bell, X, Share } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, subscribePush } from "@/push";
import { useApp } from "@/context";

const DISMISS_KEY = "rg_push_prompt_dismissed_at";
const WEEK_MS = 7 * 24 * 3600 * 1000;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent || "");
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
const inCapacitor = () =>
  !!(window.Capacitor && typeof window.Capacitor.isNativePlatform === "function" && window.Capacitor.isNativePlatform());

export default function PushEnableBanner() {
  const { user, lang } = useApp();
  const ar = lang !== "en";
  const [mode, setMode] = useState(null); // "enable" | "install-ios"
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || inCapacitor()) return;
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() - at < WEEK_MS) return;
    if (pushSupported()) {
      if (Notification.permission === "default") setMode("enable");
      else if (Notification.permission === "granted") {
        // الإذن ممنوح لكن ربما الاشتراك غير مسجل لهذا الحساب — سجّله بصمت
        subscribePush().catch(() => {});
      }
    } else if (isIos() && !isStandalone()) {
      setMode("install-ios");
    }
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setMode(null);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const r = await subscribePush();
      if (r.ok) {
        toast.success(ar ? "تم تفعيل الإشعارات 🔔 ستصلك العروض أولاً بأول" : "Notifications enabled 🔔");
        setMode(null);
      } else if (r.reason === "denied") {
        toast.error(ar ? "الإذن مرفوض — فعّله من إعدادات المتصفح" : "Permission denied — enable it in browser settings");
        dismiss();
      } else {
        toast.error(ar ? "تعذّر التفعيل — حاول مجدداً" : "Could not enable — try again");
      }
    } catch (_) {
      toast.error(ar ? "تعذّر التفعيل — حاول مجدداً" : "Could not enable");
    } finally { setBusy(false); }
  };

  if (!mode) return null;

  return (
    <div className="fixed inset-x-3 z-40 max-w-md mx-auto rounded-2xl shadow-lg border p-3"
         style={{ bottom: "calc(70px + env(safe-area-inset-bottom))", background: "var(--surface, #fff)", borderColor: "var(--border)" }}
         dir={ar ? "rtl" : "ltr"} data-testid="push-enable-banner">
      <button onClick={dismiss} className="absolute top-2 end-2 p-1 rounded-full opacity-50" data-testid="push-banner-dismiss">
        <X size={14} />
      </button>
      <div className="flex items-start gap-2.5 pe-5">
        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white" style={{ background: "var(--primary)" }}>
          <Bell size={17} />
        </div>
        {mode === "enable" ? (
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold">{ar ? "فعّل إشعارات العروض 🌹" : "Enable offer notifications 🌹"}</div>
            <div className="text-[11px] opacity-70 leading-snug">
              {ar ? "تصلك أحدث العروض وحالة طلبك فور تحديثها" : "Get offers & order updates instantly"}
            </div>
            <button onClick={enable} disabled={busy}
                    className="mt-2 rounded-full px-4 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                    style={{ background: "var(--primary)" }} data-testid="push-banner-enable-btn">
              {busy ? "…" : ar ? "🔔 تفعيل الإشعارات" : "🔔 Enable notifications"}
            </button>
          </div>
        ) : (
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold">{ar ? "أضف التطبيق لشاشتك الرئيسية 📲" : "Add to Home Screen 📲"}</div>
            <div className="text-[11px] opacity-80 leading-relaxed mt-0.5">
              {ar ? (
                <>لتصلك إشعارات العروض على الآيفون: اضغط زر المشاركة <Share size={11} className="inline mx-0.5" /> في سفاري ثم «إضافة إلى الشاشة الرئيسية»، وافتح التطبيق من الأيقونة الجديدة وفعّل الإشعارات.</>
              ) : (
                <>To receive notifications on iPhone: tap Share <Share size={11} className="inline mx-0.5" /> in Safari → “Add to Home Screen”, then open the app icon and enable notifications.</>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
